import { Save, Megaphone, Plus, Trash2, GripVertical } from "lucide-react";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";

export default function ConfigAnnouncements() {
  const { form, set, save, loading } = useSettings();
  if (!form) return <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">Loading...</div>;

  const list = Array.isArray(form.announcements) ? form.announcements : [];

  const update = (i, value) => {
    const next = list.slice();
    next[i] = value;
    set("announcements", next);
  };
  const add = () => set("announcements", [...list, "New announcement"]);
  const remove = (i) => {
    const next = list.slice();
    next.splice(i, 1);
    set("announcements", next);
  };
  const move = (i, delta) => {
    const next = list.slice();
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set("announcements", next);
  };

  return (
    <div data-testid="config-announcements-page">
      <PageHeader title="Configuration" subtitle="Announcement ticker · scrolling news bar shown on the public site" />
      <ConfigTabs />

      <div className="space-y-4 max-w-4xl">
        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Megaphone size={14} className="text-emerald-500" />
              <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Announcements</div>
            </div>
            <button onClick={add} type="button" className="h-9 px-3 bg-gray-900 hover:bg-gray-800 text-white rounded-sm text-xs font-semibold inline-flex items-center gap-1.5" data-testid="cfg-ann-add">
              <Plus size={14} /> Add announcement
            </button>
          </div>
          <div className="p-5 space-y-2.5">
            {list.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-8 border border-dashed border-gray-200 rounded-sm">
                No announcements. Click "Add announcement" to show a scrolling message to players.
              </div>
            )}
            {list.map((text, i) => (
              <div key={i} className="flex items-center gap-2 border border-gray-200 rounded-sm px-3 py-2 bg-gray-50/40" data-testid={`cfg-ann-${i}`}>
                <GripVertical size={14} className="text-gray-300 shrink-0" />
                <input
                  value={text}
                  onChange={(e) => update(i, e.target.value)}
                  className="flex-1 h-10 px-3 bg-white border border-gray-200 rounded-sm text-sm"
                  placeholder="Your announcement message…"
                  data-testid={`cfg-ann-input-${i}`}
                />
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-9 w-9 rounded-sm hover:bg-gray-100 text-gray-500 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} className="h-9 w-9 rounded-sm hover:bg-gray-100 text-gray-500 disabled:opacity-30">↓</button>
                <button type="button" onClick={() => remove(i)} className="h-9 w-9 rounded-sm hover:bg-rose-50 text-rose-500" data-testid={`cfg-ann-del-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={save}
            disabled={loading}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-ann-save"
          >
            <Save size={14} /> {loading ? "Saving..." : "Save Announcements"}
          </button>
        </div>
      </div>
    </div>
  );
}
